<template>
  <div>
    <!-- Sign In Button -->
    <b-button
      v-if="!IsAuthenticated && !btnDisabled && showBtn"
      @click="exec()"
      class="btn-premium-auth btn-signin"
    >
      <img src="https://learn.microsoft.com/en-us/azure/active-directory/develop/media/howto-add-branding-in-apps/ms-symbollockup_mssymbol_19.svg" class="ms-logo" alt="MS Logo" />
      {{ btnTxt }}
    </b-button>

    <!-- Sign Out Button -->
    <b-button
      v-if="IsAuthenticated && !btnDisabled && showBtn"
      @click="exec()"
      class="btn-premium-auth btn-signout"
    >
      <i class="el-icon-right mr-1 logout-icon"></i>
      {{ btnTxt }}
    </b-button>

    <!-- Loading State -->
    <b-button
      disabled
      v-if="btnDisabled && showBtn"
      class="btn-premium-auth btn-auth-loading"
    >
      <i class="el-icon-loading mr-2"></i>
      Authorizing...
    </b-button>

    <!-- Session/connection state lives in StatusBar.vue, rendered in the page
         layout below WelcomeUser and driven by the `statusBanner` store
         state this component commits to. -->
  </div>
</template>

<script>
import Services from '../services/main.service.vue'

// Authentication now lives entirely in the API (portal-service#209).
//
// Everything this component used to do — acquiring tokens, caching them,
// renewing them before expiry, recovering from a rejected one, and racing all
// of that against the app's own request bursts — was a consequence of the
// browser owning the Azure session. The API owns it now, so the browser's
// entire job is: send people to /auth/login, ask /auth/session who they are,
// and POST /auth/logout. There is no token here to hold, refresh, or leak.
//
// Deleted with MSAL: InitMsalClient, handleRedirectResponse, GetAllAccounts,
// refreshSession/_doRefresh/_acquireAndAuth, AuthUser, CheckAuth,
// onVisibilityWake, isAuthRedirectInFlight, signinLock, bootInProgress,
// _refreshPromise, _refreshTokenFlagHandledFor, _lastRefreshOkAt, the
// authExpiresOn bookkeeping, and the request interceptor that drove them.
export default {
  name: 'AzureAuth',
  props: {
    mobileView: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      btnTxt: 'Login with Candesic account',
      btnDisabled: false,
      showBtn: true,
      status:
        this?.$store?.getters?.appStatus ?? process?.env?.VUE_APP_STATUS ?? 'up'
    }
  },
  computed: {
    Store() {
      return this.$store.getters
    },
    UserData() {
      return this.Store.userAuth
    },
    IsAuthenticated() {
      return this.UserData.isAuthenticated
    }
  },
  mounted() {
    this.Init()

    // StatusBar renders in the page layout but has no access to the axios
    // instance, so it delegates its button back here.
    //
    // This component is mounted by Navbar when signed in and by About when
    // signed out, so it remounts across that transition. The handler is kept
    // on the instance and removed in beforeDestroy, or each remount would
    // leave a live listener bound to a destroyed instance and one click would
    // fire the action several times.
    this._statusBarHandler = (type, done = () => {}) => {
      this.handleStatusBarAction(type, done)
    }
    this.$root.$on('status-bar-action', this._statusBarHandler)

    // No request interceptor any more. It existed solely to run CheckAuth on
    // every outgoing call and decide whether a token needed renewing — work
    // the API now does per request, without a round trip.
    Services.ResInteceptors.use(
      (response) => {
        // Self-healing: a successful response proves the connection and the
        // session are working, so retire whatever the bar was showing.
        if (this.$store.getters.statusBanner) {
          this.$store.commit('clearStatusBanner')
        }
        return response
      },
      async (error) => {
        const config = error?.config ?? {}
        const status = error?.response?.status

        // 401 is now the ONLY auth failure, and it is terminal: the session is
        // gone and there is nothing client-side left to refresh. Show the bar
        // with a sign-in button rather than retrying anything.
        if (status === 401) {
          if (this.IsAuthenticated) {
            this.$store.commit('rmUser')
          }
          this.btnTxt = 'Login with Candesic account'
          this.$store.commit('setStatusBanner', 'session')
          return Promise.reject(error)
        }

        // 503 UPSTREAM_UNAVAILABLE: a dependency the API calls out to blipped.
        // Transient by definition, so retry once. Only idempotent methods — a
        // 503 can be raised after a controller has already written, so
        // replaying a POST risks a duplicate timelog or expense.
        const isIdempotent = ['get', 'head', 'options'].includes(
          String(config.method ?? 'get').toLowerCase()
        )
        if (status === 503 && isIdempotent && !config._serverRetried) {
          config._serverRetried = true
          await new Promise((resolve) => setTimeout(resolve, 1000))
          try {
            return await Services.api(config)
          } catch (retryErr) {
            console.error('Azure.upstreamRetryFailed', retryErr?.message ?? retryErr)
          }
        }

        // Connection and server state go to the persistent bar, never a toast:
        // a toast auto-hides whether or not anyone is looking, so a session
        // that died while the user was away came back as a page where
        // everything silently failed. Committing the same value repeatedly is
        // idempotent, so a burst cannot stack anything.
        if (error?.code === 'ERR_NETWORK') {
          this.$store.commit('setStatusBanner', 'network')
        } else if (status >= 500) {
          this.$store.commit('setStatusBanner', 'server')
        }
        return Promise.reject(error)
      }
    )
  },
  // Note: Vue 2 uses beforeDestroy (Vue 3 uses beforeUnmount)
  // eslint-disable-next-line vue/no-deprecated-destroyed-lifecycle
  beforeDestroy() {
    this.$root.$off('status-bar-action', this._statusBarHandler)
  },
  methods: {
    async Init() {
      this.btnDisabled = true
      try {
        if (!(await this.checkServiceHealth())) {
          this.showBtn = false
          return
        }
        await this.loadSession()
      } finally {
        this.btnDisabled = false
      }
    },

    // Single source of truth for "who is signed in". A 401 here is the normal
    // signed-out state, not an error — the API answers it for anyone without a
    // valid session cookie, including first-time visitors.
    async loadSession() {
      try {
        const {
          status,
          data: { content: user }
        } = await Services.Users.fetchSession()
        if (status === 200 && user) {
          this.$store.commit('setUser', user)
          this.$store.commit('clearStatusBanner')
          this.btnTxt = 'Sign-out'
          return true
        }
      } catch (error) {
        if (error?.response?.status !== 401) {
          console.error('Azure.loadSessionFailed', error?.message ?? error)
        }
      }
      if (this.IsAuthenticated) {
        this.$store.commit('rmUser')
      }
      this.btnTxt = 'Login with Candesic account'
      return false
    },

    exec() {
      if (this.btnDisabled) return
      if (this.IsAuthenticated) {
        this.SignOut()
      } else {
        this.SignIn()
      }
    },

    // A full-page navigation, not a popup or an iframe. The API sets its PKCE
    // state cookie on this request and hands the browser on to Microsoft, so
    // it must be a real navigation — an XHR would follow the redirect itself
    // and never show the user the sign-in page.
    SignIn() {
      this.btnDisabled = true
      const redirect = `${this.$route?.fullPath ?? '/'}`
      window.location.href = `${process.env.VUE_APP_API_BASE_URL_V2}/auth/login?redirect=${encodeURIComponent(redirect)}`
    },

    async SignOut() {
      this.btnDisabled = true
      try {
        const { data } = await Services.Users.signOut()
        this.$store.commit('rmUser')
        this.$store.commit('clearStatusBanner')
        this.btnTxt = 'Login with Candesic account'

        // Navigate to Azure's end-session endpoint so the Microsoft session
        // ends too. Without this the next sign-in click walks straight back in
        // with no prompt, which is not what someone signing out of a shared
        // machine expects. Azure returns the user to PORTAL_URL afterwards.
        const logoutUrl = data?.content?.logoutUrl
        if (logoutUrl) {
          window.location.href = logoutUrl
          return
        }
        window.location.reload()
      } catch (error) {
        console.error('Azure.signOutFailed', error?.message ?? error)
        // The server-side session is gone or unreachable either way; get the
        // user to a clean signed-out state rather than stranding them.
        window.location.reload()
      }
    },

    async handleStatusBarAction(type, done = () => {}) {
      try {
        if (type === 'session') {
          // Nothing to refresh client-side any more: the session either still
          // exists on the server or the user needs to sign in again.
          if (await this.loadSession()) {
            window.location.reload()
            return
          }
          this.SignIn()
          return
        }
        // 'network' and 'server': the dependency just needs another attempt.
        if (await this.loadSession()) {
          window.location.reload()
        }
      } catch (error) {
        console.error('Azure.statusBarActionFailed', error?.message ?? error)
      } finally {
        done()
      }
    },

    async checkServiceHealth() {
      try {
        if (this.status !== 'up') {
          this.$root.$emit('service-status', false)
          return false
        }
        const { status, data } = await Services.checkServiceHealth()
        const isAvailable = status === 200
        this.showBtn = isAvailable
        if (data?.content?.status) {
          this.$root.$emit('service-status', data.content.status)
        }
        this.$root.$emit('service-status', isAvailable)
        if (data?.content?.redirect) {
          this.$router.push(data.content.redirect)
        }
        return isAvailable
      } catch ({ message }) {
        const offline = [
          'Network Error',
          'Request failed with status code 404'
        ].includes(message)
        if (offline) {
          this.$root.$emit('service-status', false)
        } else {
          console.error('Azure.healthCheckFailed', message)
        }
        return false
      }
    }
  }
}
</script>

<style scoped>
.btn-premium-auth {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1.1rem !important;
  font-size: 0.85rem !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04) !important;
  border: 1px solid transparent !important;
}

.btn-signin {
  background-color: #ffffff !important;
  border-color: #d1d5db !important;
  color: #374151 !important;
}

.btn-signin:hover {
  background-color: #f9fafb !important;
  border-color: #9ca3af !important;
  color: #111827 !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08) !important;
}

.btn-signin:active {
  background-color: #f3f4f6 !important;
  transform: translateY(0);
}

.btn-signout {
  background-color: #f3f4f6 !important;
  border-color: #e5e7eb !important;
  color: #4b5563 !important;
}

.btn-signout:hover {
  background-color: #fee2e2 !important;
  border-color: #fca5a5 !important;
  color: #dc2626 !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(220, 38, 38, 0.08) !important;
}

.btn-signout:active {
  background-color: #fecaca !important;
  transform: translateY(0);
}

.btn-auth-loading {
  background-color: #f9fafb !important;
  border-color: #e5e7eb !important;
  color: #9ca3af !important;
  cursor: not-allowed !important;
}

.ms-logo {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  vertical-align: middle;
}

.logout-icon {
  font-size: 1.05rem;
  vertical-align: middle;
}
</style>
